import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { GraphQLContactPreference } from '@sola/schema/dotcom/supporters/schema.generated.ts';

// These are the preferences that are currently showin in the sheet.
// Email Preferences - emailInterestCategories
// Account & Transactions - Account_Transaction
// Your Sponsored Child Updates - Sponsored_Child_Communication
// Your Impact - Your_Impact
// Compassion Communications - Compassion_Communication
// Help Us Improve - Help_Us_Improve
// Compassion Prayer Network - Compassion_Prayer_Network
// Compassion Explorer Magazine - Compassion_Explorer_Magazine_for_Kids
// Volunteering and Visits - Volunteering_and_Visits

// Text Preferences - textInterestCategories
// Account & Transactions - Account_Transaction
// Your Sponsored Child Updates - Sponsored_Child_Communication
// Your Impact - Your_Impact
// Volunteering and Visits - Volunteering_and_Visits

/**
 * Account & Transactions category is excluded from the summary — users
 * can't change it from the settings sheet (they must call support), but
 * it's still shown in the modal. We treat it as invisible for the
 * "Some / No" summary logic.
 */
const excludedCategory = 'Account_Transaction';

const hasNonExcludedCategory = (
  categories: readonly string[] | null | undefined,
): boolean =>
  (categories ?? []).some((category) => category !== excludedCategory);

export const useComponentContactCard = (
  preferences?: GraphQLContactPreference | null,
) => {
  const { t } = useTranslation(['my-settings']);

  // TODO: Should we be only including the categories for text and email that we know are showing in the sheet to be edited?
  // So if there is a category added in SF and we aren't allowing the user to edit it and the user doesn't have any other email categories
  // do we show "Some emails"? Seems like this would be confusing for the supporter.

  const preferencesSummary = useMemo(() => {
    // `doNotContact` is a global opt-out; `doNotEmail` / `doNotText` are
    // channel-specific. Either one suppresses that channel regardless of
    // what interest categories are set.
    const doNotContact = preferences?.doNotContact ?? false;
    const emailOptedOut = doNotContact || (preferences?.doNotEmail ?? false);
    const textOptedOut = doNotContact || (preferences?.doNotText ?? false);

    const hasEmailPrefs =
      !emailOptedOut &&
      hasNonExcludedCategory(preferences?.emailInterestCategories);
    const hasTextPrefs =
      !textOptedOut &&
      hasNonExcludedCategory(preferences?.textInterestCategories);

    const emailPart = hasEmailPrefs
      ? t('my-settings:contactCard.preferencesSummary.someEmail')
      : t('my-settings:contactCard.preferencesSummary.noEmail');
    const textPart = hasTextPrefs
      ? t('my-settings:contactCard.preferencesSummary.someText')
      : t('my-settings:contactCard.preferencesSummary.noText');

    return `${emailPart}, ${textPart}`;
  }, [preferences, t]);

  return { preferencesSummary };
};
